import React, { FC, FormEventHandler, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useResendVerificationMail } from 'src/hooks/api/sendVerification';
import { RSDetailHeader } from 'src/reservation-page/detail-header/RSDetailHeader';
import { RSButton } from 'src/reservation-page/RSButton';
import { RSAnchor } from 'src/reservation-page/RSAnchor';
import { RSToastContainer } from 'src/reservation-page/toast/RSToastContainer';
import { toast } from 'src/reservation-page/toast';
import { useGetReservationFlowMe } from 'src/hooks/api/reservation-flow/useGetReservationFlowMe';

import KantanLogo from 'src/templates/merchants/resources/book/components/logo/kantan-logo.svg';
import { buildUrl } from 'src/utils/request-url';

export const EmailVerifyTemplate: FC = () => {
  const intl = useIntl();
  const { data: user } = useGetReservationFlowMe();

  const { isValidating, resendVerificationMail } = useResendVerificationMail();

  const onFormSubmit = useCallback<FormEventHandler<HTMLFormElement>>(
    async (event) => {
      event.preventDefault();

      const result = await resendVerificationMail();

      if (!result.success) {
        toast({
          variant: 'error',
          title: intl.formatMessage({
            defaultMessage: '本人認証メールの送信に失敗しました。',
            id: 'emYpzy',
            description:
              'src/templates/merchants/EmailVerifyTemplate/index.tsx',
          }),
        });
        return;
      }

      toast({
        variant: 'success',
        title: intl.formatMessage({
          defaultMessage: '本人認証メールを送信しました',
          id: 'Lh54yY',
          description: 'src/templates/merchants/EmailVerifyTemplate/index.tsx',
        }),
        description: intl.formatMessage({
          defaultMessage: 'メールをご確認ください',
          id: '1YuR/d',
          description: 'src/templates/merchants/EmailVerifyTemplate/index.tsx',
        }),
      });
    },
    [intl, resendVerificationMail],
  );

  return (
    <div>
      <RSToastContainer />
      <div className="z-5 sticky top-0">
        <RSDetailHeader
          title={
            <div>
              <KantanLogo
                width={119}
                height={32}
                viewBox="0 0 119 32"
                className="block"
              />
            </div>
          }
        />
      </div>

      <div className="pc:px-4 pc:pt-4 mx-auto box-border max-w-[560px] px-2.5 pb-5 pt-3">
        <div className="mb-4">
          <div className="space-y-5">
            <div className="space-y-3">
              <div>
                <h2 className="rs-font text-24 text-gray-1000 pc:text-28 m-0 font-bold">
                  {intl.formatMessage({
                    defaultMessage: '本人認証メールを送信しました',
                    id: 'Lh54yY',
                    description:
                      'src/templates/merchants/EmailVerifyTemplate/index.tsx',
                  })}
                </h2>
              </div>

              <div>
                <div className="rs-font text-16 text-gray-1000 pc:text-15 font-normal">
                  {intl.formatMessage(
                    {
                      defaultMessage:
                        'メールアドレス確認のため {userEmail} に本人認証メールを送信しました。<br></br>メールをご確認いただき、メールに記載されたURLから認証を完了してください。',
                      id: 'dlMwwn',
                      description:
                        'src/templates/merchants/EmailVerifyTemplate/index.tsx',
                    },
                    {
                      userEmail: user?.email,
                      br: () => <br></br>,
                    },
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <div>
                <div className="rs-font text-16 text-gray-1000 pc:text-15 font-bold">
                  {intl.formatMessage({
                    defaultMessage: 'メールが届かない場合',
                    id: 'ma+ODu',
                    description:
                      'src/templates/merchants/EmailVerifyTemplate/index.tsx',
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <div>
                  <div className="rs-font text-16 text-gray-1000 pc:text-15  font-normal">
                    {intl.formatMessage({
                      defaultMessage:
                        'メールアドレス確認メールが届かない場合、以下をご確認ください。',
                      id: '4Z6Z9H',
                      description:
                        'src/templates/merchants/EmailVerifyTemplate/index.tsx',
                    })}
                  </div>
                </div>

                <div>
                  <ul className="rs-font text-16 text-gray-1000 pc:text-15  font-normal">
                    <li>
                      {intl.formatMessage(
                        {
                          defaultMessage:
                            '登録にご使用のメールアドレス {userEmail} が正しいかご確認ください。正しくない場合は、メールアドレスを再設定してください。{emailEditLink}から再設定が可能です。',
                          id: 'iQ+DCK',
                          description:
                            'src/templates/merchants/EmailVerifyTemplate/index.tsx',
                        },
                        {
                          userEmail: user?.email,
                          emailEditLink: (
                            <RSAnchor href={buildUrl('/u/settings/email/edit')}>
                              こちら
                            </RSAnchor>
                          ),
                        },
                      )}
                    </li>

                    <li>
                      {intl.formatMessage({
                        defaultMessage:
                          '携帯メールアドレスや一部のフリーメールアドレスでは、メールが届かない場合がございます。その場合、「@coubic.com」からのメールを受信できるように受信許可設定をご変更ください。',
                        id: 'CrDgHt',
                        description:
                          'src/templates/merchants/EmailVerifyTemplate/index.tsx',
                      })}
                    </li>

                    <li>
                      {intl.formatMessage({
                        defaultMessage:
                          '迷惑メールフォルダに振り分けられていたり、フィルターや転送の設定によって受信ボックス以外の場所に保管されていないかご確認ください。',
                        id: '3ZGnZE',
                        description:
                          'src/templates/merchants/EmailVerifyTemplate/index.tsx',
                      })}
                    </li>

                    <li>
                      {intl.formatMessage({
                        defaultMessage:
                          'メールの配信に時間がかかる場合がございます。数分程度待ってから、メールが届いているか再度ご確認ください。',
                        id: 'Wf+FDG',
                        description:
                          'src/templates/merchants/EmailVerifyTemplate/index.tsx',
                      })}
                    </li>
                  </ul>
                </div>

                <div>
                  <div className="rs-font text-16 pc:text-15 font-normal">
                    <RSAnchor href="https://reserve-faq.stores.jp/hc/ja/articles/28754125263129">
                      {intl.formatMessage({
                        defaultMessage: '詳しいメール認証の手順はこちら',
                        id: 'wjFJwL',
                        description:
                          'src/templates/merchants/EmailVerifyTemplate/index.tsx',
                      })}
                    </RSAnchor>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div>
          <form onSubmit={onFormSubmit}>
            <RSButton
              variant="outline"
              disabled={isValidating}
              isLoading={isValidating}
              isBlock
            >
              {intl.formatMessage({
                defaultMessage: '認証メールを再送信する',
                id: '4BIJGV',
                description:
                  'src/templates/merchants/EmailVerifyTemplate/index.tsx',
              })}
            </RSButton>
          </form>
        </div>
      </div>
    </div>
  );
};
